import type { InvoiceImportEvidence } from "./invoice-settlement";
export const DATE_RANGE_REPORT_KINDS = ["invoice-details", "payments-collected", "tax-summary", "revenue-by-client"] as const;
export type DateRangeReportKind = typeof DATE_RANGE_REPORT_KINDS[number];
export const REPORT_KINDS = [...DATE_RANGE_REPORT_KINDS, "accounts-aging"] as const;
export type ReportKind = typeof REPORT_KINDS[number];
export const INVOICE_REPORT_STATUSES = ["Issued", "Sent", "Overdue", "Paid", "Archived"] as const;
export type InvoiceReportStatus = typeof INVOICE_REPORT_STATUSES[number];

export interface ReportFilters {
  from: string;
  to: string;
  clientId: string | null;
  status: InvoiceReportStatus | null;
}

export const AGING_BUCKETS = ["current", "1-30", "31-60", "61-90", "90-plus"] as const;
export type AgingBucket = typeof AGING_BUCKETS[number];
export const AGING_BUCKET_LABELS: Record<AgingBucket, string> = {
  current: "Current", "1-30": "1–30 days", "31-60": "31–60 days", "61-90": "61–90 days", "90-plus": "90+ days",
};

export interface AgingSelection {
  clientId: string | null;
  bucket: AgingBucket | "all" | null;
}
export type ReportingFilters = ReportFilters | AgingSelection;

export interface AgingInvoiceRow {
  invoiceId: string;
  number: string | null;
  clientId: string;
  clientName: string | null;
  issueDate: string;
  dueDate: string;
  daysPastDue: number;
  bucket: AgingBucket;
  totalMinor: number;
  paidMinor: number;
  dueMinor: number;
  archived: boolean;
}

export interface AgingAmounts {
  buckets: Record<AgingBucket, number>;
  totalMinor: number;
}

export interface AgingClientRow extends AgingAmounts {
  clientId: string;
  clientName: string | null;
}

export interface AccountsAgingReport {
  kind: "accounts-aging";
  currency: "USD";
  currentDate: string;
  filters: AgingSelection;
  rows: AgingClientRow[];
  summary: AgingAmounts;
  invoices: AgingInvoiceRow[];
  details: AgingInvoiceRow[];
}

export interface ReportTax {
  id: string;
  name: string;
  rateBasisPoints: number;
  taxableBaseMinor: number;
  amountMinor: number;
}

export interface InvoiceDetailsLine {
  lineId: string | null;
  position: number;
  title: string;
  details: string;
  unitAmountMinor: number | null;
  quantityThousandths: number | null;
  // The accepted document's line Amount, before document discount and taxes.
  lineTotalMinor: number;
  taxes: ReportTax[];
}

export interface InvoiceDetailsRow {
  importEvidence?: InvoiceImportEvidence;
  sourceReportedSettlementMinor?: number;
  invoiceId: string;
  number: string | null;
  clientId: string | null;
  clientName: string | null;
  issueDate: string;
  dueDate: string;
  status: InvoiceReportStatus;
  totalMinor: number;
  paidMinor: number;
  dueMinor: number;
  lines: InvoiceDetailsLine[];
  taxes: ReportTax[];
}

export interface PaymentCollectedRow {
  paymentId: string;
  receivedDate: string;
  clientId: string | null;
  clientName: string | null;
  invoiceId: string;
  number: string | null;
  reference: string | null;
  amountMinor: number;
}

export interface InvoiceDetailsReport {
  kind: "invoice-details";
  currency: "USD";
  dateBasis: "issue-date";
  filters: ReportFilters;
  summary: { invoiceCount: number; totalInvoicedMinor: number; paidMinor: number; dueMinor: number; sourceReportedSettlementMinor?: number };
  rows: InvoiceDetailsRow[];
}

export interface PaymentsCollectedReport {
  kind: "payments-collected";
  currency: "USD";
  dateBasis: "received-date";
  filters: ReportFilters;
  summary: { totalReceivedMinor: number };
  rows: PaymentCollectedRow[];
}

export interface TaxSummaryRow {
  // Snapshot identity includes saved-rate provenance, name, rate and tax number,
  // never the document-local definition ID.
  key: string;
  name: string;
  rateBasisPoints: number;
  taxNumber: string | null;
  savedRateId: string | null;
  invoiceCount: number;
  taxableBaseMinor: number;
  taxChargedMinor: number;
}

export interface TaxSummaryDetail extends Omit<TaxSummaryRow, "invoiceCount"> {
  invoiceId: string;
  number: string | null;
  issueDate: string;
  clientId: string | null;
  clientName: string | null;
  archived: boolean;
}

export interface TaxSummaryReport {
  kind: "tax-summary";
  currency: "USD";
  dateBasis: "issue-date";
  filters: ReportFilters;
  summary: { invoicesWithTax: number; totalTaxChargedMinor: number };
  rows: TaxSummaryRow[];
  details: TaxSummaryDetail[];
}

export interface RevenueByClientRow {
  clientId: string;
  clientName: string | null;
  invoiceCount: number;
  issuedBillingMinor: number;
  percentOfTotalTenths: number;
}

export interface RevenueByClientReport {
  kind: "revenue-by-client";
  currency: "USD";
  dateBasis: "issue-date";
  filters: ReportFilters;
  summary: { clientCount: number; invoiceCount: number; totalIssuedBillingMinor: number };
  rows: RevenueByClientRow[];
}

export type DateRangeReportingResult = InvoiceDetailsReport | PaymentsCollectedReport | TaxSummaryReport | RevenueByClientReport;
export type ReportingResult = DateRangeReportingResult | AccountsAgingReport;

export function formatReportPercent(tenths: number): string {
  return `${Math.floor(tenths / 10)}.${tenths % 10}%`;
}

export function formatRevenuePercent(row: Pick<RevenueByClientRow, "issuedBillingMinor" | "percentOfTotalTenths">, totalIssuedBillingMinor: number): string {
  return row.issuedBillingMinor > 0 && BigInt(row.issuedBillingMinor) * 1000n < BigInt(totalIssuedBillingMinor)
    ? "<0.1%" : formatReportPercent(row.percentOfTotalTenths);
}

export function csvCell(value: string | number): string {
  const text = String(value);
  const safe = typeof value === "string" && /^[\s]*[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${safe.replaceAll('"', '""')}"`;
}

const money = (value: number) => (value / 100).toFixed(2);
const integerMoney = (value: number) => `${BigInt(value) / 100n}.${(BigInt(value) % 100n).toString().padStart(2, "0")}`;
const quantity = (value: number | null) => value === null ? "" : String(value / 1000);

export function formatRevenueBilling(value: number): string {
  const [whole, fraction] = integerMoney(value).split(".");
  return `$${new Intl.NumberFormat("en-US").format(BigInt(whole!))}.${fraction}`;
}

// Serialize the loaded result itself. The UI and API never run another filter
// or calculation to export a report.
export function reportingCsv(report: ReportingResult): string {
  const rows: (string | number)[][] = report.kind === "accounts-aging"
    ? [["Client", "Invoice #", "Issue Date", "Due Date", "Days Past Due", "Aging Bucket", "Invoice Total", "Amount Paid", "Amount Due", "Archived"],
      ...(report.filters.bucket === null ? report.invoices : report.details).map(invoice => [
        invoice.clientName ?? "", invoice.number ?? "", invoice.issueDate, invoice.dueDate, invoice.daysPastDue, AGING_BUCKET_LABELS[invoice.bucket],
        money(invoice.totalMinor), money(invoice.paidMinor), money(invoice.dueMinor), invoice.archived ? "yes" : "no",
      ])]
    : report.kind === "revenue-by-client"
    ? [["Client", "Invoice Count", "Issued Billing", "Percent of Total"],
      ...report.rows.map(client => [client.clientName ?? "", client.invoiceCount, integerMoney(client.issuedBillingMinor), formatReportPercent(client.percentOfTotalTenths)])]
    : report.kind === "tax-summary"
    ? [["Issue Date", "Invoice #", "Client", "Tax Name", "Rate", "Tax Number", "Taxable Base", "Tax Charged", "Archived"],
      ...report.details.map(tax => [tax.issueDate, tax.number ?? "", tax.clientName ?? "", tax.name, `${tax.rateBasisPoints / 100}%`, tax.taxNumber ?? "",
        money(tax.taxableBaseMinor), money(tax.taxChargedMinor), tax.archived ? "yes" : "no",
      ])]
    : report.kind === "invoice-details"
    ? [["Invoice ID", "Invoice #", "Client ID", "Client", "Issue Date", "Due Date", "Status", "Currency", "Invoice Total", "Amount Paid", "Amount Due", "Line ID", "Line Position", "Line Title", "Line Details", "Rate", "Quantity", "Line Total", "Taxes", "Import source", "Source status", "Source Date Paid", "Source-reported settlement"],
      ...report.rows.flatMap(invoice => invoice.lines.map(line => [
        invoice.invoiceId, invoice.number ?? "", invoice.clientId ?? "", invoice.clientName ?? "", invoice.issueDate, invoice.dueDate, invoice.status, report.currency,
        money(invoice.totalMinor), money(invoice.paidMinor), money(invoice.dueMinor), line.lineId ?? "", line.position, line.title, line.details,
        line.unitAmountMinor === null ? "" : money(line.unitAmountMinor), quantity(line.quantityThousandths), money(line.lineTotalMinor), JSON.stringify(line.taxes), invoice.importEvidence?.format ?? "", invoice.importEvidence?.status ?? "", invoice.importEvidence?.paidOn ?? "", money(invoice.sourceReportedSettlementMinor ?? 0),
      ]))]
    : [["Payment ID", "Received Date", "Client ID", "Client", "Invoice ID", "Invoice #", "Reference", "Currency", "Amount"],
      ...report.rows.map(payment => [payment.paymentId, payment.receivedDate, payment.clientId ?? "", payment.clientName ?? "", payment.invoiceId, payment.number ?? "", payment.reference ?? "", report.currency, money(payment.amountMinor)])];
  return rows.map(row => row.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

export function reportingFilename(report: ReportingResult): string {
  if (report.kind === "accounts-aging") return `signalbill-accounts-aging-${report.currentDate}.csv`;
  const { from, to } = report.filters;
  return `signalbill-${report.kind}-${from}-to-${to}.csv`;
}
